import {
  ApolloClient,
  ApolloLink,
  InMemoryCache
} from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { setContext } from '@apollo/client/link/context'
import {
  getMainDefinition,
  relayStylePagination
} from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import { createClient } from 'graphql-ws'

import {
  SERVICE_ACCESS_PIN_HEADER,
  SERVICE_ACCESS_PIN_STORAGE_KEY
} from 'shared/constants/serviceAccess'
import { isProd } from 'shared/helpers/env'
import { redirectToGradooLogin } from 'shared/utils/auth'

import { isJWTExpired } from './utils/jwt'
import { signInToLayoutCreator } from './utils/login'
import {
  getValidGradooTokens,
  refreshLayoutCreatorToken
} from './utils/refresh'

const headers = {
  'keep-alive': 'true'
}

const layoutCreatorLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_URL,
  headers
})

const layoutCreatorWsUrl = (
  process.env.REACT_APP_GRAPHQL_WS_URL ||
  (process.env.REACT_APP_GRAPHQL_URL || '').replace(/^http/i, 'ws')
).replace(/\/+$/, '')

const layoutCreatorWsLink = new GraphQLWsLink(
  createClient({
    url: layoutCreatorWsUrl,
    connectionParams: async () => {
      const lcToken = localStorage.getItem('lcToken')
      const adminToken = localStorage.getItem('adminToken')
      const serviceAccessPin = localStorage.getItem(
        SERVICE_ACCESS_PIN_STORAGE_KEY
      )

      return {
        authorization: lcToken ? `Bearer ${lcToken}` : '',
        admin: adminToken || '',
        [SERVICE_ACCESS_PIN_HEADER]: serviceAccessPin || ''
      }
    }
  })
)

const layoutCreatorAuthLink = setContext(async (_, { headers }) => {
  try {
    const gradooTokens = await getValidGradooTokens()
    const lcToken = localStorage.getItem('lcToken')
    const lcRefreshToken = localStorage.getItem('lcRefreshToken')
    const adminToken = localStorage.getItem('adminToken')
    const serviceAccessPin = localStorage.getItem(
      SERVICE_ACCESS_PIN_STORAGE_KEY
    )

    const generateResult = (token: string) => ({
      headers: {
        ...headers,
        authorization: 'Bearer ' + (token || ''),
        admin: adminToken,
        [SERVICE_ACCESS_PIN_HEADER]: serviceAccessPin || ''
      }
    })

    if (!gradooTokens) {
      await redirectToGradooLogin()
    }

    if (!lcToken || !lcRefreshToken) {
      const { token } = await signInToLayoutCreator()
      return generateResult(token)
    }

    if (isJWTExpired(lcToken)) {
      if (isJWTExpired(lcRefreshToken)) {
        const { token } = await signInToLayoutCreator()
        return generateResult(token)
      }
      const token = await refreshLayoutCreatorToken()
      return generateResult(token)
    }

    return generateResult(lcToken)
  } catch (e) {
    if (!isProd()) {
      console.error(e)
    }
    redirectToGradooLogin()
  }
})

const gradooUri = process.env.REACT_APP_GRADOO_GRAPHQL_URL as string

export const gradooAuthLink = setContext(async (_, { headers }) => {
  try {
    const tokens = await getValidGradooTokens()
    if (!tokens) throw new Error()

    return {
      headers: {
        ...headers,
        Authorization: tokens.token ? `JWT ${tokens.token}` : ''
      }
    }
  } catch (e) {
    if (!isProd()) {
      console.error(e)
    }
    redirectToGradooLogin()
  }
})

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        collagesPhotos: relayStylePagination(['collagesAlbum']),
        quotesInstances: relayStylePagination(['moduleInstance', 'orderBy']),
        rankingsQuestions: relayStylePagination(['moduleInstance']),
        customPagesInstancesResult: relayStylePagination(['moduleInstance'])
      }
    }
  }
})

const gradooLink = createUploadLink({
  uri: gradooUri
})

const httpLink = ApolloLink.split(
  operation => operation.getContext().client === 'gradoo',
  ApolloLink.from([gradooAuthLink, gradooLink as unknown as ApolloLink]),
  ApolloLink.from([layoutCreatorAuthLink, layoutCreatorLink])
)

const link = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  layoutCreatorWsLink,
  httpLink
)

const client = new ApolloClient({
  cache,
  link
})

export default client
